// ADMIN-FAHRT-ZUWEISUNG-001 — EIN Zuweisungs-Panel für zwei Orte: Fahrt-Detail („Fahrten") und
// Dispatch-Platz („Zuweisung"). Der Server (lib/adminReassign) entscheidet Eignung und Zuweisung;
// dieses Panel zeigt seine Kandidaten, holt EINE ausdrückliche Bestätigung und meldet das Ergebnis.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowLeftRight, Car, Circle, RefreshCw, Search, UserCheck } from 'lucide-react';
import api from '../services/api';
import './RideReassign.css';

export const RIDE_STATUS_LABEL = {
  searching: 'Suche',
  accepted: 'Angenommen',
  arrived: 'Angekommen',
  in_progress: 'Unterwegs',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
};

const km = (value) => (value == null ? '—' : `${Number(value).toLocaleString('de-DE', { maximumFractionDigits: 1 })} km`);
const terminalStates = ['expired', 'declined', 'invalid'];
const resolvesAttempt = (offer, attempt) => attempt && terminalStates.includes(offer?.state)
  && offer.request_id === attempt.id && offer.ride_id === attempt.rideId
  && offer.driver_id === attempt.driverId && offer.assignment_version === attempt.assignmentVersion;

export default function RideReassign({ rideId, onReassigned, compact = false }) {
  const [data, setData] = useState(null);         // { ride, candidates } vom Server
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [driverId, setDriverId] = useState('');
  const [reason, setReason] = useState('');
  const [query, setQuery] = useState('');
  const [onlyEligible, setOnlyEligible] = useState(true);
  const [saving, setSaving] = useState(false);
  const requestRef = useRef(null);
  const submitting = useRef(false);
  const loadSequence = useRef(0);
  const activeRide = useRef(rideId);
  const activeSession = useRef(localStorage.getItem('admin_token'));
  const dataSession = useRef(undefined);

  const load = useCallback(() => {
    if (!rideId || activeRide.current !== rideId) return;
    const session = localStorage.getItem('admin_token');
    if (activeSession.current !== session) {
      activeSession.current = session; dataSession.current = undefined;
      requestRef.current = null; submitting.current = false;
      setData(null); setDriverId(''); setReason(''); setSaving(false);
    }
    const sequence = ++loadSequence.current;
    setLoading(true);
    setError(null);
    api.get(`/admin/rides/${rideId}/reassign-candidates`)
      .then((response) => {
        if (sequence !== loadSequence.current || activeRide.current !== rideId
            || session !== localStorage.getItem('admin_token')) return;
        const attempt = requestRef.current;
        if (attempt?.session === session && response.data?.ride?.id === attempt.rideId
            && response.data.ride.assignment_version === attempt.assignmentVersion
            && resolvesAttempt(response.data.handover_offer, attempt)) {
          requestRef.current = null; submitting.current = false; setSaving(false);
        }
        dataSession.current = session;
        setData(response.data); setLoading(false);
      })
      .catch((requestError) => {
        if (sequence !== loadSequence.current || session !== localStorage.getItem('admin_token')) return;
        setError(requestError.response?.data?.message || requestError.response?.data?.error || 'Kandidaten konnten nicht geladen werden.');
        setLoading(false);
      });
  }, [rideId]);

  useEffect(() => {
    activeRide.current = rideId;
    setData(null); setDriverId(''); setReason(''); setSaving(false);
    requestRef.current = null; submitting.current = false; dataSession.current = undefined; load();
    return () => { activeRide.current = null; loadSequence.current += 1; requestRef.current = null; submitting.current = false; };
  }, [rideId, load]);

  const pending = data?.handover_offer?.state === 'pending';
  useEffect(() => {
    if (!pending) return undefined;
    const timer = setInterval(load, 2000);
    return () => clearInterval(timer);
  }, [pending, load]);

  if (!rideId) return null;

  const ride = data?.ride || null;
  const candidates = data?.candidates || [];
  const hasDriver = Boolean(ride?.driver_id);
  const verb = hasDriver ? 'An anderen Fahrer übergeben' : 'Fahrer zuweisen';
  const needle = query.trim().toLowerCase();
  const visible = candidates.filter((candidate) => {
    if (onlyEligible && !candidate.eligible && !candidate.is_current) return false;
    if (!needle) return true;
    return [candidate.name, candidate.company_name, candidate.license_plate, candidate.vehicle_brand, candidate.vehicle_model]
      .some((value) => String(value || '').toLowerCase().includes(needle));
  });
  const chosen = candidates.find((candidate) => candidate.id === driverId) || null;

  const submit = async (event) => {
    event.preventDefault();
    if (!chosen || submitting.current || pending) return;
    const session = localStorage.getItem('admin_token');
    if (dataSession.current !== session) { load(); return; }
    const confirmation = hasDriver
      ? `Fahrt von ${ride.driver_name || 'dem bisherigen Fahrer'} an ${chosen.name} übergeben?\n\nDer bisherige Fahrer behält die Fahrt, bis der neue Fahrer das klingelnde Angebot annimmt. Dessen normale Auto-Annahme-Einstellung gilt. Bei Ablehnung oder Ablauf bleibt die Fahrt beim bisherigen Fahrer.`
      : `Fahrt ${chosen.name} anbieten?\n\nDer Fahrer erhält das normale klingelnde Angebot. Seine Auto-Annahme-Einstellung gilt. Erst bei Annahme wird die Fahrt zugewiesen und der Kunde benachrichtigt.`;
    if (!window.confirm(confirmation)) return;
    const submission = {};
    submitting.current = submission;
    setSaving(true);
    try {
      const binding = JSON.stringify([rideId, ride.assignment_version, chosen.id, reason.trim()]);
      if (requestRef.current?.binding !== binding || requestRef.current.session !== session) requestRef.current = {
        binding, id: globalThis.crypto.randomUUID(), session,
        rideId, driverId: chosen.id, assignmentVersion: ride.assignment_version,
      };
      const attempt = requestRef.current;
      const response = await api.post(`/admin/rides/${rideId}/reassign`, {
        driver_id: chosen.id,
        handover_request_id: requestRef.current.id,
        expected_assignment_version: ride.assignment_version,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      if (submitting.current !== submission || activeRide.current !== rideId
          || session !== localStorage.getItem('admin_token')) return;
      if (terminalStates.includes(response.data?.handover_offer?.state)) {
        if (resolvesAttempt(response.data.handover_offer, attempt)) requestRef.current = null;
        toast.error('Das vorherige Angebot ist beendet. Bitte erneut anbieten.');
        load(); return;
      }
      const offered = response.status === 202 || response.data?.handover_offer?.state === 'pending';
      const assigned = response.data?.handover_offer?.state === 'accepted'
        && response.data?.ride?.id === rideId && response.data.ride.driver_id === chosen.id
        && ['accepted', 'arrived', 'in_progress'].includes(response.data.ride.status);
      if (!offered && !assigned) {
        toast.error('Die Zuweisung ist nicht bestätigt. Bitte aktualisieren.');
        load(); return;
      }
      toast.success(`${offered ? 'Übergabe angeboten. Annahme durch den Fahrer steht noch aus.' : (response.data?.message || 'Fahrt zugewiesen')} — ${chosen.name}`);
      requestRef.current = null;
      setDriverId('');
      setReason('');
      if (onReassigned) onReassigned(response.data);
      load();
    } catch (requestError) {
      if (submitting.current !== submission || activeRide.current !== rideId
          || session !== localStorage.getItem('admin_token')) return;
      toast.error(requestError.response?.data?.message || requestError.response?.data?.error || 'Zuweisung abgelehnt');
      load();   // der Server ist die Wahrheit — Kandidaten und Fahrtlage frisch holen
    } finally {
      if (submitting.current === submission && session === localStorage.getItem('admin_token')) {
        submitting.current = false; setSaving(false);
      }
    }
  };

  return (
    <section className={`ride-reassign${compact ? ' is-compact' : ''}`} aria-label="Fahrer zuweisen">
      <header className="ride-reassign-head">
        <span><ArrowLeftRight size={16} /></span>
        <div>
          <small>Dispatch</small>
          <h3>{verb}</h3>
        </div>
        <button type="button" className="ride-reassign-refresh" onClick={load} disabled={loading} aria-label="Kandidaten aktualisieren">
          <RefreshCw size={13} /> {loading ? 'Lädt…' : 'Aktualisieren'}
        </button>
      </header>

      {error && <p className="ride-reassign-note is-error">{error}</p>}

      {pending && <p className="ride-reassign-note" role="status">
        {hasDriver ? <>Übergabeangebot läuft. {ride.driver_name || 'Der bisherige Fahrer'} behält die Fahrt bis zur Annahme.</> : 'Fahrtangebot läuft. Die Annahme durch den Fahrer steht noch aus.'}
      </p>}

      {terminalStates.includes(data?.handover_offer?.state) && <p className="ride-reassign-note" role="status">
        {data.handover_offer.state === 'expired' ? 'Das Fahrtangebot ist abgelaufen.' : data.handover_offer.state === 'declined' ? 'Der Fahrer hat das Fahrtangebot abgelehnt.' : 'Das Fahrtangebot ist nicht mehr möglich.'} {hasDriver ? 'Der bisherige Fahrer behält die Fahrt.' : 'Es wurde kein Fahrer zugewiesen. Bitte erneut anbieten.'}
      </p>}

      {ride && !ride.assignable && (
        <p className="ride-reassign-note">{ride.reason_text || 'Diese Fahrt kann hier nicht zugewiesen werden.'}</p>
      )}

      {ride && ride.assignable && (
        <form onSubmit={submit} className="ride-reassign-form">
          <p className="ride-reassign-current">
            {hasDriver
              ? <>Aktuell bei <strong>{ride.driver_name}</strong> · Status {RIDE_STATUS_LABEL[ride.status] || ride.status}</>
              : <>Noch kein Fahrer{ride.offered_driver_name ? <> · Angebot läuft bei <strong>{ride.offered_driver_name}</strong></> : null} · Status {RIDE_STATUS_LABEL[ride.status] || ride.status}</>}
          </p>

          <div className="ride-reassign-tools">
            <label className="ride-reassign-search">
              <Search size={13} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Fahrer, Betrieb oder Kennzeichen"
                aria-label="Kandidaten durchsuchen"
              />
            </label>
            <label className="ride-reassign-toggle">
              <input type="checkbox" checked={onlyEligible} onChange={(event) => setOnlyEligible(event.target.checked)} />
              Nur geeignete
            </label>
          </div>

          <ul className="ride-reassign-list" role="listbox" aria-label="Fahrer-Kandidaten">
            {loading && candidates.length === 0 && <li className="ride-reassign-empty">Kandidaten werden geladen …</li>}
            {!loading && visible.length === 0 && (
              <li className="ride-reassign-empty">{candidates.length === 0 ? 'Keine freigegebenen Fahrer vorhanden.' : 'Kein Fahrer passt zu Filter oder Suche.'}</li>
            )}
            {visible.map((candidate) => {
              const disabled = !candidate.eligible;
              const selected = candidate.id === driverId;
              return (
                <li key={candidate.id}>
                  <label className={`ride-reassign-candidate${selected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}${candidate.is_current ? ' is-current' : ''}`}>
                    <input
                      type="radio"
                      name={`reassign-${rideId}`}
                      value={candidate.id}
                      checked={selected}
                      disabled={disabled}
                      onChange={() => setDriverId(candidate.id)}
                      aria-label={`${candidate.name} wählen`}
                    />
                    <span className={`ride-reassign-dot${candidate.is_online ? ' is-online' : ''}`} title={candidate.is_online ? 'Online' : 'Offline'}><Circle size={9} /></span>
                    <span className="ride-reassign-who">
                      <strong>{candidate.name}{candidate.is_current ? ' (aktuell)' : ''}</strong>
                      <small>
                        {candidate.company_name || 'Ohne Betrieb'}
                        {candidate.license_plate ? <> · <Car size={11} /> {candidate.vehicle_brand} {candidate.vehicle_model} · {candidate.license_plate}</> : null}
                      </small>
                    </span>
                    <span className="ride-reassign-meta">
                      <b>{km(candidate.distance_km)}</b>
                      <small>
                        {candidate.is_online ? 'online' : 'offline'}
                        {candidate.ride_status ? ` · ${RIDE_STATUS_LABEL[candidate.ride_status] || candidate.ride_status}` : ' · frei'}
                      </small>
                    </span>
                    {disabled && !candidate.is_current && <em className="ride-reassign-why">{candidate.reason_text || candidate.reason}</em>}
                  </label>
                </li>
              );
            })}
          </ul>

          <div className="ride-reassign-actions">
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Grund (optional), z. B. Fahrer im Stau"
              aria-label="Grund der Zuweisung"
              maxLength={200}
            />
            <button type="submit" disabled={!chosen || saving || pending}>
              <UserCheck size={14} /> {saving ? (hasDriver ? 'Sendet Angebot…' : 'Weist zu…') : (chosen ? `${verb}: ${chosen.name}` : verb)}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
